import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { restaurantsAPI } from '../../services/api'
import { EyeIcon, EyeSlashIcon, UserIcon, LockClosedIcon, KeyIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { UtensilsCrossed, ShoppingBag, CalendarCheck, TrendingUp } from 'lucide-react'
import { t, LanguageSwitcher } from '../../i18n'
import BrandLogo from '../../components/BrandLogo'

const HIGHLIGHTS = [
  { icon: UtensilsCrossed, title: 'Restaurants', text: 'QR menus, POS, kitchen screen and reservations.' },
  { icon: ShoppingBag, title: 'Online stores', text: 'Products, stock, cart and online payments.' },
  { icon: CalendarCheck, title: 'Bookings', text: 'Appointments and rooms with live availability.' },
]

function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, pinLogin, isAuthenticated } = useAuth()

  const [loginMode, setLoginMode] = useState('password') // 'password' or 'pin'
  const [formData, setFormData] = useState({ username: '', password: '', pin: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const from = location.state?.from?.pathname || '/admin/dashboard'

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true })
  }, [isAuthenticated, navigate, from])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const result = loginMode === 'password'
        ? await login({ username: formData.username, password: formData.password })
        : await pinLogin({ username: formData.username, pin: formData.pin })

      if (result.success) {
        const userStr = localStorage.getItem('user')
        const user = userStr ? JSON.parse(userStr) : null
        // Owners who run a single store land straight on that store's own
        // admin dashboard - the multi-store /owner console only makes sense
        // once there's more than one store to manage.
        if (user?.isSuperAdmin) {
          // The platform operator lands on the platform console.
          navigate(location.state?.from?.pathname || '/platform', { replace: true })
        } else if (user?.role === 'owner') {
          try {
            const { data } = await restaurantsAPI.getRestaurants()
            navigate(data.length === 1 ? '/admin/dashboard' : '/owner/dashboard', { replace: true })
          } catch {
            navigate('/owner/dashboard', { replace: true })
          }
        } else {
          navigate(from, { replace: true })
        }
      }
    } catch (error) {
      console.error('Login error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const switchMode = mode => {
    setLoginMode(mode)
    setFormData(prev => ({ ...prev, password: '', pin: '' }))
    setShowPassword(false)
  }

  const secretId = loginMode === 'password' ? 'password' : 'pin'

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Form */}
      <div className="flex flex-col px-6 sm:px-12 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo imageClassName="h-11 w-11" nameClassName="font-bold text-lg text-gray-900" />
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto py-10">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('Welcome back')}</h1>
            <p className="mt-2 text-gray-600">{t('Sign in to manage your business, website and orders.')}</p>

            <div role="tablist" aria-label={t('Sign-in method')} className="mt-8 grid grid-cols-2 p-1 rounded-xl bg-gray-100">
              {[['password', 'Password'], ['pin', 'Staff PIN']].map(([mode, label]) => (
                <button key={mode} type="button" role="tab" aria-selected={loginMode === mode} onClick={() => switchMode(mode)}
                  className={`py-2.5 rounded-lg text-sm font-semibold transition ${loginMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
                  {t(label)}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">{t('Username')}</label>
                <div className="relative">
                  <UserIcon className="h-5 w-5 text-gray-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                  <input id="username" name="username" type="text" autoComplete="username" required autoFocus
                    value={formData.username} onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-300 ps-10 pe-3 py-3 text-gray-900 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 outline-none"
                    placeholder={t('Enter your username')} />
                </div>
              </div>

              <div>
                <label htmlFor={secretId} className="block text-sm font-medium text-gray-700 mb-1.5">{loginMode === 'password' ? t('Password') : t('PIN')}</label>
                <div className="relative">
                  {loginMode === 'password'
                    ? <LockClosedIcon className="h-5 w-5 text-gray-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                    : <KeyIcon className="h-5 w-5 text-gray-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />}
                  <input id={secretId} name={secretId}
                    type={loginMode === 'password' && !showPassword ? 'password' : loginMode === 'pin' ? 'password' : 'text'}
                    inputMode={loginMode === 'pin' ? 'numeric' : undefined}
                    autoComplete={loginMode === 'password' ? 'current-password' : 'off'} required
                    value={loginMode === 'password' ? formData.password : formData.pin} onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-300 ps-10 pe-11 py-3 text-gray-900 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 outline-none"
                    placeholder={loginMode === 'password' ? t('Enter your password') : t('Enter your PIN')} />
                  {loginMode === 'password' && (
                    <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? t('Hide password') : t('Show password')}
                      className="absolute inset-y-0 end-0 px-3 flex items-center text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  )}
                </div>
                {loginMode === 'pin' && <p className="mt-1.5 text-xs text-gray-500">{t('Staff PIN for quick sign-in at the POS or kitchen.')}</p>}
              </div>

              <button type="submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 text-white font-semibold py-3.5 hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition">
                {loading ? <><span className="loading-spinner" />{t('Signing in...')}</> : <>{t('Sign in')}<ArrowRightIcon className="h-4 w-4 rtl-flip" /></>}
              </button>
            </form>

            <p className="mt-8 text-sm text-gray-600 text-center">
              {t('New to Rawaj Business?')}{' '}
              <Link to="/get-started" className="font-semibold text-gray-900 hover:underline">{t('Create your business')}</Link>
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-center">© {new Date().getFullYear()} {t("Rawaj Business")}</p>
      </div>

      {/* Showcase */}
      <div className="hidden lg:flex relative overflow-hidden bg-gray-950 text-white p-12 flex-col justify-between">
        <div aria-hidden="true" className="absolute -top-32 -end-32 h-96 w-96 rounded-full bg-blue-500/30 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-40 -start-20 h-96 w-96 rounded-full bg-sky-500/30 blur-3xl" />

        <div className="relative">
          <p className="text-blue-300 text-sm font-semibold">{t('One platform, every kind of business')}</p>
          <h2 className="mt-3 text-4xl font-bold leading-tight max-w-md">{t('Run your restaurant, store or bookings from one place.')}</h2>
        </div>

        {/* Mini dashboard preview */}
        <div className="relative my-10 max-w-md">
          <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm text-white/70">{t("Today's sales")}</p>
              <span className="inline-flex items-center gap-1 text-xs text-blue-300"><TrendingUp size={14} />+18%</span>
            </div>
            <p className="mt-1 text-3xl font-bold">12,480.00</p>
            <div className="mt-4 flex items-end gap-1.5 h-16" aria-hidden="true">
              {[40, 55, 35, 70, 60, 85, 75, 95, 65, 80, 90, 100].map((h, i) => (
                <span key={i} className="flex-1 rounded-t bg-blue-400/80" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <p className="text-xs text-white/60">{t('New orders')}</p>
              <p className="text-2xl font-bold mt-1">36</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <p className="text-xs text-white/60">{t('Reservations')}</p>
              <p className="text-2xl font-bold mt-1">14</p>
            </div>
          </div>
        </div>

        <ul className="relative grid gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-3">
              <span className="h-10 w-10 flex-shrink-0 rounded-xl bg-white/10 grid place-items-center"><Icon size={18} /></span>
              <div>
                <p className="font-semibold">{t(title)}</p>
                <p className="text-sm text-white/60">{t(text)}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default LoginPage
